import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Share2, Linkedin, Copy, GitBranch, MoreHorizontal, Pencil, Trash2, Share } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { UserBadges } from '@/components/ui/user-badges';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { MarkdownPreview } from '@/components/ui/markdown-preview';
import { DiffViewer } from '@/components/ui/diff-viewer';
import ReactionPicker from './ReactionPicker';
import { getProfileUrl, getCommitUrl, getBranchUrl, getTagUrl } from '../../utils/urls';
import { setNavState } from '../../utils/navState';
import { formatRelativeDate } from '../../utils/format';
import { TAKEAWAY_CONFIG, type Commit } from '../../types/api';

const TYPE_STYLES: Record<string, string> = {
  feat: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  fix: 'bg-red-500/10 text-red-500 border-red-500/30',
  docs: 'bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/30',
  chore: 'bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]',
};

// Default style for custom/unknown types
const DEFAULT_TYPE_STYLE = 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30';

function getTypeStyle(type: string): string {
  return TYPE_STYLES[type] ?? DEFAULT_TYPE_STYLE;
}


interface CommitCardProps {
  commit: Commit | Partial<Commit>;
  onShare?: (commit: Commit) => void;
  onEdit?: (commit: Commit) => void;
  onDelete?: (commit: Commit) => void;
  currentUserId?: string;
  isPreview?: boolean;
}

export default function CommitCard({ commit, onShare, onEdit, onDelete, currentUserId, isPreview = false }: CommitCardProps) {
  const navigate = useNavigate();
  const isOwner = currentUserId && commit.userId === currentUserId;
  const reactionCounts = commit.reactionCounts ?? { heart: 0, celebration: 0, fire: 0, sleep: 0, dynamite: 0 };

  const { relative: formattedDate, time: formattedTime } = formatRelativeDate(
    commit.createdAt || new Date().toISOString()
  );

  const handleCopyLink = async () => {
    if (!commit.id) return;
    try {
      await navigator.clipboard.writeText(getCommitUrl(commit.id, true));
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  // Detect mobile for native share
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleNativeShare = async () => {
    if (!commit.id || !navigator.share) return;
    try {
      await navigator.share({
        title: `${commit.type}: ${commit.message}`,
        text: commit.body || `${commit.type}: ${commit.message}`,
        url: getCommitUrl(commit.id, true),
      });
    } catch (err) {
      // User cancelled or share failed - don't show error for cancel
      if ((err as Error).name !== 'AbortError') {
        toast.error('Failed to share');
      }
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        {/* Header with user info and timestamp */}
        {commit.user && (
          <div className="flex justify-between items-start mb-4">
            {isPreview ? (
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={commit.user.avatarUrl} alt={commit.user.displayName || commit.user.username} />
                  <AvatarFallback className="font-mono">
                    {commit.user.displayName?.[0] || commit.user.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium leading-none">
                    {commit.user.displayName || commit.user.username}
                    <UserBadges badges={commit.user.badges} className="ml-1" />
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] font-mono mt-1">
                    @{commit.user.username}
                  </p>
                </div>
              </div>
            ) : (
              <a
                href={getProfileUrl(commit.user.username)}
                onClick={() => commit.user && setNavState({ user: commit.user })}
                className="flex items-center gap-3 no-underline"
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={commit.user.avatarUrl} alt={commit.user.displayName || commit.user.username} />
                  <AvatarFallback className="font-mono">
                    {commit.user.displayName?.[0] || commit.user.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium leading-none">
                    {commit.user.displayName || commit.user.username}
                    <UserBadges badges={commit.user.badges} className="ml-1" />
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] font-mono mt-1">
                    @{commit.user.username}
                  </p>
                </div>
              </a>
            )}
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
              {commit.branch && commit.branch !== 'main' && commit.user && (
                isPreview ? (
                  <Badge
                    variant="outline"
                    className="font-mono text-xs bg-[hsl(var(--muted))]/50"
                  >
                    <GitBranch className="h-3 w-3 mr-1" />
                    {commit.branch}
                  </Badge>
                ) : (
                  <a
                    href={getBranchUrl(commit.user.username, commit.branch)}
                    onClick={() => commit.user && setNavState({ user: commit.user })}
                  >
                    <Badge
                      variant="outline"
                      className="font-mono text-xs bg-[hsl(var(--muted))]/50 hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer"
                    >
                      <GitBranch className="h-3 w-3 mr-1" />
                      {commit.branch}
                    </Badge>
                  </a>
                )
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] font-mono whitespace-nowrap">
                    {formattedDate}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {formattedDate} at {formattedTime}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}

        {/* Commit message */}
        <div className="pt-px">
          <p className={`font-mono font-medium ${!commit.message && isPreview ? 'text-[hsl(var(--muted-foreground))]' : ''}`}>
            {commit.type && (
              <>
                <Badge
                  variant="outline"
                  className={`font-mono text-sm ${getTypeStyle(commit.type)}`}
                >
                  {commit.type}
                </Badge>
                <span className="mr-1.5">:</span>
              </>
            )}
            {commit.message || (isPreview ? 'Your commit message...' : '')}
          </p>
        </div>

        {/* Body content - always use MarkdownPreview for consistent rendering */}
        {commit.body && (
          <div className="mt-4 p-4 bg-[hsl(var(--muted))] rounded-md border-l-[3px] border-[hsl(var(--border))] prose prose-sm dark:prose-invert max-w-none">
            <MarkdownPreview content={commit.body} />
          </div>
        )}

        {/* Takeaways section */}
        {commit.takeaways?.items && commit.takeaways.items.length > 0 && (
          <div className="mt-4 rounded-md overflow-hidden border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]">
            {commit.takeaways.items.map((item, i) => {
              const config = TAKEAWAY_CONFIG[item.type];
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                  <span className={config.color}>{config.symbol}</span>
                  <span className="text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Diff viewer */}
        {commit.snippet?.before && (
          <div className="mt-4">
            <DiffViewer
              before={commit.snippet.before}
              after={commit.snippet.after}
              language={commit.snippet.language}
            />
          </div>
        )}

        {/* Tags */}
        {commit.tags && commit.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-4">
            {commit.tags.map((tag) => (
              isPreview ? (
                <Badge key={tag} variant="secondary" className="font-mono text-xs">
                  #{tag}
                </Badge>
              ) : (
                <Link key={tag} to={getTagUrl(tag)}>
                  <Badge variant="secondary" className="font-mono text-xs hover:bg-[hsl(var(--muted-foreground))]/20 transition-all cursor-pointer">
                    #{tag}
                  </Badge>
                </Link>
              )
            ))}
          </div>
        )}

        {/* Actions footer */}
        {!isPreview && (
          <div className="flex justify-between items-center mt-4 pt-3 border-t border-[hsl(var(--border))]">
            <div className="flex items-center gap-4">
              <ReactionPicker
                commitId={commit.id!}
                reactionCounts={reactionCounts}
                userReactions={commit.userReactions}
                onSignInRequired={!currentUserId ? () => navigate('/login') : undefined}
              />
            </div>

            <div className="flex items-center gap-0 sm:gap-1">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onShare?.(commit as Commit)}>
                    <Linkedin className="mr-2 h-4 w-4" />
                    Share to LinkedIn
                  </DropdownMenuItem>
                  {isMobile && typeof navigator !== 'undefined' && 'share' in navigator ? (
                    <DropdownMenuItem onClick={handleNativeShare}>
                      <Share className="mr-2 h-4 w-4" />
                      Share...
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={handleCopyLink}>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy link
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {isOwner && (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit?.(commit as Commit)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete?.(commit as Commit)}
                      className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
